"use client";

import { cn } from "@/lib/utils";

export type GlowVariant = "rainbow" | "emerald" | "purple" | "blue" | "amber";
export type GlowIntensity = "subtle" | "medium" | "strong";
export type GlowRounded = "lg" | "xl" | "2xl";

export interface GlowBorderProps {
  /** Color variant of the glow */
  variant?: GlowVariant;
  /** Intensity of the glow effect */
  intensity?: GlowIntensity;
  /** Border radius */
  rounded?: GlowRounded;
  /** Only show glow on hover (default: true) */
  showOnHover?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Child elements */
  children: React.ReactNode;
}

const gradients: Record<GlowVariant, string> = {
  rainbow: "linear-gradient(135deg, #34d399, #3b82f6, #a855f7)",
  emerald: "linear-gradient(135deg, #34d399, #10b981, #059669)",
  purple: "linear-gradient(135deg, #a855f7, #8b5cf6, #7c3aed)",
  blue: "linear-gradient(135deg, #3b82f6, #2563eb, #1d4ed8)",
  amber: "linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)",
};

const intensityClasses: Record<GlowIntensity, string> = {
  subtle: "opacity-25",
  medium: "opacity-50",
  strong: "opacity-75",
};

const roundedClasses: Record<GlowRounded, string> = {
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
};

/**
 * GlowBorder - Animated gradient border effect
 *
 * Creates a glowing gradient border effect around children.
 * Uses CSS mask technique for the border effect.
 *
 * @example
 * // Rainbow glow on hover
 * <GlowBorder variant="rainbow" showOnHover>
 *   <Card>Content</Card>
 * </GlowBorder>
 *
 * // Always visible emerald glow
 * <GlowBorder variant="emerald" showOnHover={false} intensity="medium">
 *   <Card>Content</Card>
 * </GlowBorder>
 */
export function GlowBorder({
  variant = "rainbow",
  intensity = "medium",
  rounded = "2xl",
  showOnHover = true,
  className,
  children,
}: GlowBorderProps) {
  const gradient = gradients[variant];

  return (
    <div className={cn("relative group", className)}>
      {/* Content */}
      {children}

      {/* Glow border overlay */}
      <div
        className={cn(
          "absolute inset-0 pointer-events-none border-2 border-transparent transition-opacity duration-500",
          roundedClasses[rounded],
          showOnHover
            ? cn(
                "opacity-0 group-hover:opacity-100",
                intensityClasses[intensity].replace(
                  "opacity-",
                  "group-hover:opacity-"
                )
              )
            : intensityClasses[intensity]
        )}
        style={{
          background: `${gradient} border-box`,
          WebkitMask:
            "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
        aria-hidden="true"
      />
    </div>
  );
}

export default GlowBorder;
